import { useEffect } from "react";
import { NullableHeightSize, Position, Size, Stroke } from "../types";
import { useTemplatesStatusStore } from "../store/useTemplatesStatusStore";

const ARROW_MOVE_STEP = 1;

export const useMoveItemWithArrows = <
  SizeType extends Size | NullableHeightSize = Size | NullableHeightSize,
>(
  id: string | undefined,
  isCurrentItem: boolean,
  position: Position,
  size: SizeType,
  strokeWidth: number,
  previewWidth: number,
  previewHeight: number,
  onMove: (params: {
    id?: string;
    position?: Position;
    size?: SizeType;
    stroke?: Stroke;
  }) => void
) => {
  const { addTemplateUpdate } = useTemplatesStatusStore((state) => ({
    addTemplateUpdate: state.addTemplateUpdate,
  }));

  useEffect(() => {
    if (!isCurrentItem) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)
      ) {
        return;
      }

      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      e.preventDefault();

      const strokeInPixels = Math.round(strokeWidth * previewWidth);

      const step = ARROW_MOVE_STEP;
      const newPosition = {
        x: Math.round(position.x * previewWidth),
        y: Math.round(position.y * previewHeight),
      };
      switch (e.key) {
        case "ArrowUp":
          newPosition.y = (newPosition.y - step) / previewHeight;
          newPosition.x = newPosition.x / previewWidth;
          break;
        case "ArrowDown":
          newPosition.y = (newPosition.y + step) / previewHeight;
          newPosition.x = newPosition.x / previewWidth;
          break;
        case "ArrowLeft":
          newPosition.x = (newPosition.x - step) / previewWidth;
          newPosition.y = newPosition.y / previewHeight;
          break;
        case "ArrowRight":
          newPosition.x = (newPosition.x + step) / previewWidth;
          newPosition.y = newPosition.y / previewHeight;
          break;
      }

      if (newPosition.x - strokeWidth < 0) {
        newPosition.x = 0 + strokeWidth;
      }
      if (newPosition.x + size.width + strokeWidth > 1) {
        newPosition.x = 1 - size.width - strokeWidth;
      }

      if (newPosition.y - strokeInPixels / previewHeight < 0) {
        newPosition.y = 0 + strokeWidth;
      }
      if (
        newPosition.y + (size.height ?? 0) + strokeInPixels / previewHeight >
        1
      ) {
        newPosition.y = 1 - (size.height ?? 0) - strokeWidth;
      }

      if (id) {
        onMove({
          id,
          position: newPosition,
        });
      } else {
        onMove({
          position: newPosition,
        });
      }
      addTemplateUpdate();
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, [
    addTemplateUpdate,
    id,
    isCurrentItem,
    onMove,
    position,
    previewHeight,
    previewWidth,
    size.height,
    size.width,
    strokeWidth,
  ]);
};
